// หน้าแรก (การตลาด) — โครงแบบ Lorka: hero + ช่องถาม / โมเดล / เครื่องมือ / บทบาท / เทียบราคา / แพ็กเกจ / รีวิว / FAQ
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { CHAT_MODELS, IMAGE_MODELS, VIDEO_MODELS } from "../services/ai/models";
import { baht } from "../services/pricing";
import { PlanCards, PricingFaq } from "./app/PricingView";
import { Badge, Button, Icon, IconButton, Logo, ModelMark } from "../components/ui";

const FEATURES = [
  { icon: "chat", t: "แชทหลายโมเดลในห้องเดียว", d: "สลับ GPT, Claude, Gemini, DeepSeek และอีกมาก ได้ทุกข้อความ เทียบคำตอบได้ทันที" },
  { icon: "shield", t: "AI ในเครื่อง ข้อมูลไม่รั่ว", d: "โมเดลขนาดเล็กรันบนเครื่องคุณผ่านเบราว์เซอร์ ข้อความไม่ถูกส่งออกไปไหน" },
  { icon: "image", t: "สร้างและแก้ไขภาพ", d: "เปลี่ยนไอเดียเป็นภาพด้วยโมเดลภาพชั้นนำ แก้ภาพด้วยคำสั่งเดียว", soon: true },
  { icon: "video", t: "สร้างวิดีโอสั้น", d: "ข้อความหรือภาพนิ่ง กลายเป็นวิดีโอคุณภาพสูง", soon: true },
  { icon: "wand", t: "เครื่องมือ AI สำเร็จรูป", d: "Humanizer, AI Detector, แปลภาษา, สรุปความ, เขียนอีเมล, แคปชันโซเชียล" },
  { icon: "zap", t: "จ่ายแบบที่ใช่", d: "สมาชิกรายเดือน/รายปี หรือจ่ายตามการใช้งาน token จริง ไม่มีค่าแรกเข้า" },
];

const ROLES = [
  { id: "biz", icon: "bulb", t: "ผู้ประกอบการ", d: "วางแผนธุรกิจ เขียนแผนการตลาด ตอบลูกค้า และวิเคราะห์คู่แข่งได้ในไม่กี่นาที", ex: ["ร่างแผนธุรกิจร้านกาแฟ 1 หน้า", "เขียนข้อความตอบรีวิวลูกค้า", "วิเคราะห์จุดแข็ง-จุดอ่อนคู่แข่ง"], m: ["gpt", "claude", "deepseek"] },
  { id: "mkt", icon: "sparkles", t: "นักการตลาด", d: "แคปชัน โฆษณา บทความ SEO และไอเดียคอนเทนต์ไม่มีวันหมด", ex: ["แคปชันโปรโมชั่น 11.11", "หัวข้อบทความ SEO 20 หัวข้อ", "สคริปต์วิดีโอ 30 วินาที"], m: ["claude", "gemini", "grok"] },
  { id: "stu", icon: "brain", t: "นักเรียน นักศึกษา", d: "สรุปบทเรียน อธิบายเรื่องยากให้ง่าย ฝึกภาษา และเตรียมสอบ", ex: ["สรุปบทที่ 3 เป็นข้อๆ", "อธิบายแคลคูลัสแบบเข้าใจง่าย", "ตรวจแกรมม่าเรียงความ"], m: ["gemini", "qwen-max", "gpt"] },
  { id: "dev", icon: "code", t: "โปรแกรมเมอร์", d: "เขียน อธิบาย และหาบั๊กโค้ดได้ทุกภาษา พร้อมแนะนำแนวทางที่ดีกว่า", ex: ["เขียน API ด้วย Node.js", "หาบั๊กใน SQL query", "รีวิวโค้ด React"], m: ["claude", "deepseek", "gpt"] },
  { id: "res", icon: "search", t: "นักวิจัย", d: "ค้นคว้าข้อมูล สรุปงานวิจัย และเปรียบเทียบแหล่งข้อมูลพร้อมอ้างอิง", ex: ["สรุปงานวิจัยจาก PDF", "เปรียบเทียบทฤษฎี 2 แบบ", "หาแหล่งอ้างอิงล่าสุด"], m: ["sonar", "gemini", "claude"] },
  { id: "data", icon: "chart", t: "นักวิเคราะห์ข้อมูล", d: "เขียนสูตร Excel, Python, SQL และแปลผลข้อมูลให้เข้าใจง่าย", ex: ["สูตร Excel หายอดรวมตามเงื่อนไข", "Python วิเคราะห์ยอดขาย", "สรุป insight จากตาราง"], m: ["gpt", "deepseek", "qwen-max"] },
];

const COMPARE = [
  ["ChatGPT Plus", 700], ["Claude Pro", 700], ["Gemini Advanced", 690], ["SuperGrok", 1050], ["Perplexity Pro", 700], ["Mistral Pro", 520],
];

const REVIEWS = [
  ["ปิยะนุช ส.", "Content Creator", "เมื่อก่อนจ่ายค่า AI 3 เจ้า ตอนนี้เหลือที่เดียว แถมสลับโมเดลในแชทเดียวได้ สะดวกมาก"],
  ["ธนากร ว.", "Software Engineer", "ชอบที่มีโมเดลรันในเครื่อง เอาโค้ดลูกค้ามาถามได้สบายใจ ไม่ต้องกลัวข้อมูลหลุด"],
  ["ศิริพร ก.", "เจ้าของร้านออนไลน์", "ใช้ทำแคปชันขายของทุกวัน แพ็กเกจ Pro คุ้มมาก จ่ายผ่าน QR ง่ายดี"],
  ["ณัฐวุฒิ ป.", "นักศึกษาปริญญาโท", "สรุปเปเปอร์ภาษาอังกฤษเป็นไทยได้ดีมาก จ่ายตาม token ใช้น้อยก็จ่ายน้อย"],
];

export default function Landing() {
  const { isLoggedIn, pricing } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [role, setRole] = useState(ROLES[0].id);
  const [cycle, setCycle] = useState("monthly");
  const [menu, setMenu] = useState(false);
  const r = ROLES.find((x) => x.id === role);
  const cloud = CHAT_MODELS.filter((m) => m.kind === "cloud");
  const all = [...cloud, ...IMAGE_MODELS, ...VIDEO_MODELS];
  const totalSep = COMPARE.reduce((n, [, p]) => n + p, 0);
  const pro = pricing.plans.find((p) => p.id === "pro") || pricing.plans[1];
  const ask = (e) => {
    e.preventDefault();
    navigate("/chat", { state: { prompt: q } });
  };
  const go = () => navigate(isLoggedIn ? "/chat" : "/register");

  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-in">
          <Link to="/" className="lp-logo"><Logo size={32} /></Link>
          <nav className={"lp-links" + (menu ? " is-open" : "")} onClick={() => setMenu(false)}>
            <a href="#models">โมเดล</a>
            <a href="#features">ฟีเจอร์</a>
            <a href="#roles">ใช้ทำอะไรได้</a>
            <a href="#pricing">ราคา</a>
            <a href="#faq">คำถามที่พบบ่อย</a>
          </nav>
          <div className="lp-actions">
            <IconButton icon={theme === "dark" ? "sun" : "moon"} label="สลับธีม" onClick={toggle} />
            {isLoggedIn ? null : <Link to="/login" className="btn btn-ghost btn-md lp-signin">เข้าสู่ระบบ</Link>}
            <Button variant="primary" iconRight="arrowRight" onClick={go}>{isLoggedIn ? "เข้าใช้งาน" : "ลองใช้ฟรี"}</Button>
            <IconButton icon="menu" label="เมนู" className="lp-burger" onClick={() => setMenu((m) => !m)} />
          </div>
        </div>
      </header>

      <section className="lp-hero">
        <div className="lp-glow" />
        <div className="lp-wrap center">
          <div className="welcome-badge"><Icon name="sparkles" size={15} /> AI กว่า 20 โมเดล · บัญชีเดียว · จ่ายที่เดียว</div>
          <h1>แพลตฟอร์ม AI ครบวงจร<br /><span className="grad">สำหรับงานเขียน ค้นคว้า ศิลปะ</span> และอีกมากมาย</h1>
          <p className="lp-sub">รวม GPT, Claude, Gemini, Grok, DeepSeek และโมเดลชั้นนำไว้ในแชทเดียว ไม่ต้องสมัครหลายเจ้า ประหยัดกว่า {Math.round((1 - pro.monthly / totalSep) * 100)}%</p>
          <form className="lp-ask" onSubmit={ask}>
            <Icon name="sparkles" size={20} className="brand" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ถามอะไรก็ได้... เช่น ช่วยเขียนแผนการตลาดร้านเบเกอรี่" aria-label="ถาม AI" />
            <button type="submit" className="send-btn" aria-label="ถาม"><Icon name="send" size={18} stroke={2.4} /></button>
          </form>
          <div className="lp-hero-chips">
            {["เขียนอีเมลสมัครงาน", "สรุปข่าววันนี้", "แปลเอกสารเป็นอังกฤษ", "ไอเดียคอนเทนต์"].map((t) => (
              <button key={t} onClick={() => navigate("/chat", { state: { prompt: t } })}>{t}</button>
            ))}
          </div>
          <div className="lp-proof">
            <div className="lp-avatars">{["ป", "ธ", "ศ", "ณ", "ก"].map((c, i) => <span key={i} style={{ "--i": i }}>{c}</span>)}</div>
            <div>
              <div className="lp-stars">{[0, 1, 2, 3, 4].map((i) => <Icon key={i} name="star" size={14} />)} <b>4.9/5</b></div>
              <span className="small muted">ผู้ใช้กว่า 12,000 คนไว้วางใจ</span>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-marquee" aria-label="ผู้ให้บริการโมเดล">
        <div className="lp-marquee-track">
          {[...all, ...all].map((m, i) => (
            <span key={i} className="lp-marquee-item"><ModelMark model={m} size={26} />{m.name}</span>
          ))}
        </div>
      </section>

      <section className="lp-sec" id="models">
        <div className="lp-wrap">
          <div className="lp-head">
            <Badge tone="brand">โมเดลแชท</Badge>
            <h2>โมเดล AI ที่ดีที่สุด รวมไว้ในที่เดียว</h2>
            <p className="muted">เลือกโมเดลที่เก่งที่สุดสำหรับแต่ละงาน — หรือใช้โมเดลในเครื่องเมื่อต้องการความเป็นส่วนตัวสูงสุด</p>
          </div>
          <div className="lp-models">
            {CHAT_MODELS.slice(4, 10).map((m) => (
              <div key={m.id} className="lp-model">
                <div className="lp-model-top">
                  <ModelMark model={m} size={46} />
                  <div>
                    <b>{m.name}</b>
                    <div className="faint small">{m.provider}</div>
                  </div>
                </div>
                <p>{m.desc}</p>
                <div className="model-tags">{m.tags.map((t) => <em key={t}>{t}</em>)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-sec lp-alt" id="features">
        <div className="lp-wrap">
          <div className="lp-head">
            <Badge tone="brand">ฟีเจอร์</Badge>
            <h2>ทำได้มากกว่าแชท</h2>
            <p className="muted">ครบทุกเครื่องมือที่ต้องใช้ในงานประจำวัน</p>
          </div>
          <div className="lp-features">
            {FEATURES.map((f) => (
              <div key={f.t} className="lp-feature">
                <span className="lp-feature-ic"><Icon name={f.icon} size={22} /></span>
                <b>{f.t} {f.soon ? <Badge tone="soft">เร็วๆ นี้</Badge> : null}</b>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-sec" id="roles">
        <div className="lp-wrap">
          <div className="lp-head">
            <Badge tone="brand">สำหรับทุกคน</Badge>
            <h2>MixAI ช่วยงานคุณได้อย่างไร</h2>
          </div>
          <div className="lp-roles">
            <div className="lp-role-tabs">
              {ROLES.map((x) => (
                <button key={x.id} className={x.id === role ? "is-active" : ""} onClick={() => setRole(x.id)}>
                  <Icon name={x.icon} size={18} /> {x.t}
                </button>
              ))}
            </div>
            <div className="lp-role-body">
              <h3>{r.t}</h3>
              <p className="muted">{r.d}</p>
              <div className="lp-role-ex">
                {r.ex.map((e) => (
                  <button key={e} onClick={() => navigate("/chat", { state: { prompt: e } })}>
                    <Icon name="chat" size={16} /> {e} <Icon name="arrowRight" size={15} />
                  </button>
                ))}
              </div>
              <div className="lp-role-models">
                <span className="small muted">โมเดลที่เหมาะ:</span>
                {r.m.map((id) => {
                  const m = CHAT_MODELS.find((x) => x.id === id);
                  return m ? <span key={id} className="lp-role-model"><ModelMark model={m} size={22} />{m.name}</span> : null;
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-sec lp-alt">
        <div className="lp-wrap">
          <div className="lp-head">
            <Badge tone="brand">ประหยัดกว่า</Badge>
            <h2>เลิกจ่ายหลายเจ้า จ่ายที่เดียวพอ</h2>
          </div>
          <div className="lp-compare">
            <div className="lp-compare-col">
              <div className="lp-compare-title">สมัครแยกทีละเจ้า</div>
              {COMPARE.map(([n, p]) => (
                <div key={n} className="lp-compare-row"><span>{n}</span><span>{baht(p)}/เดือน</span></div>
              ))}
              <div className="lp-compare-total"><span>รวม</span><s>{baht(totalSep)}/เดือน</s></div>
            </div>
            <div className="lp-compare-vs">VS</div>
            <div className="lp-compare-col is-mix">
              <div className="lp-ribbon">ยอดนิยม</div>
              <Logo size={36} />
              <div className="lp-compare-price"><b>{baht(pro.monthly)}</b>/เดือน</div>
              <div className="lp-save">ประหยัด {baht(totalSep - pro.monthly)} ต่อเดือน</div>
              <ul className="plan-features">
                <li><Icon name="check" size={15} stroke={2.4} /> เข้าถึงโมเดลชั้นนำทุกค่ายในที่เดียว</li>
                <li><Icon name="check" size={15} stroke={2.4} /> โมเดลในเครื่อง ปลอดภัยสูงสุด</li>
                <li><Icon name="check" size={15} stroke={2.4} /> เครื่องมือ AI ครบทุกตัว</li>
                <li><Icon name="check" size={15} stroke={2.4} /> ไม่มีค่าแรกเข้า ยกเลิกได้ทุกเมื่อ</li>
              </ul>
              <Button variant="primary" block size="lg" onClick={go}>เริ่มใช้งานเลย</Button>
            </div>
          </div>
          <p className="tiny faint center mt-16">* ราคาเทียบโดยประมาณจากค่าสมาชิกรายเดือนของแต่ละบริการ อาจเปลี่ยนแปลงตามผู้ให้บริการ</p>
        </div>
      </section>

      <section className="lp-sec" id="pricing">
        <div className="lp-wrap">
          <div className="lp-head">
            <Badge tone="brand">ราคา</Badge>
            <h2>ราคาที่เข้าใจง่าย</h2>
            <p className="muted">เลือกสมาชิกรายเดือน/รายปี หรือ <Link to="/pricing#tokens">จ่ายตามการใช้งาน token</Link> เริ่มต้น {baht(pricing.payg.minBaht)} · หรือ <b>ปรับขนาดแพ็กเกจเอง</b> ด้วยแถบเลื่อน</p>
          </div>
          <PlanCards pricing={pricing} onChoose={() => navigate(isLoggedIn ? "/pricing" : "/register")} cycle={cycle} setCycle={setCycle} />
        </div>
      </section>

      <section className="lp-sec lp-alt">
        <div className="lp-wrap">
          <div className="lp-head">
            <Badge tone="brand">รีวิว</Badge>
            <h2>ทำไมผู้ใช้เลือก MixAI</h2>
          </div>
          <div className="lp-reviews">
            {REVIEWS.map(([n, role2, t]) => (
              <figure key={n} className="lp-review">
                <div className="lp-stars">{[0, 1, 2, 3, 4].map((i) => <Icon key={i} name="star" size={14} />)}</div>
                <blockquote>“{t}”</blockquote>
                <figcaption><span className="avatar" style={{ width: 34, height: 34 }}>{n[0]}</span><span><b>{n}</b><span className="tiny faint">{role2}</span></span></figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-sec" id="faq">
        <div className="lp-wrap lp-narrow">
          <div className="lp-head">
            <h2>คำถามที่พบบ่อย</h2>
          </div>
          <PricingFaq />
        </div>
      </section>

      <section className="lp-cta">
        <div className="lp-wrap center">
          <h2>พร้อมใช้ AI ทุกค่ายในที่เดียวหรือยัง?</h2>
          <p>สมัครฟรีวันนี้ รับ 40,000 token ใช้งานได้ทันที ไม่ต้องใช้บัตรเครดิต</p>
          <Button variant="light" size="lg" iconRight="arrowRight" onClick={go}>เริ่มใช้งานฟรี</Button>
        </div>
      </section>

      <footer className="lp-foot">
        <div className="lp-wrap lp-foot-in">
          <div>
            <Logo size={28} />
            <p className="small muted mt-8">รวมโมเดล AI ชั้นนำไว้ในที่เดียว</p>
          </div>
          <div className="lp-foot-links">
            <div><b>ผลิตภัณฑ์</b><Link to="/chat">แชท AI</Link><Link to="/tools">เครื่องมือ</Link><Link to="/models">โมเดล</Link><Link to="/pricing">ราคา</Link></div>
            <div><b>บริษัท</b><a href="#about">เกี่ยวกับเรา</a><a href="#contact">ติดต่อ</a></div>
            <div><b>กฎหมาย</b><a href="#terms">ข้อกำหนดการใช้งาน</a><a href="#privacy">นโยบายความเป็นส่วนตัว</a><a href="#refund">นโยบายคืนเงิน</a></div>
          </div>
        </div>
        <div className="lp-wrap lp-copy tiny faint">© {new Date().getFullYear()} MixAI · ชื่อและเครื่องหมายของโมเดลเป็นของเจ้าของแต่ละราย</div>
      </footer>
    </div>
  );
}
